import { supabase } from '../clients/supabaseClient';
import type {
    Order,
    OrderFormData,
    OrderItem,
    OrderStatus,
    Payment,
    WalletTransaction,
    OrderPageRow,
    OrdersNeedingDriverRow,
    OrderItemRow,
    CakeOrder,
    CakeOrderWithOrder,
    CakeLinkedOrder,
    CakeOrderCustomer,
    CakeSize,
    CakeSizePayload,
    CakeOptionPayload,
    CakeOptionPrice,
    CakeOptionPriceUpsert,
    CakeOption,
    OrderItemHistory,
    PickingSessionItemRow
} from '../types';
import { OPEN_ORDER_STATUSES, ORDER_STATUS } from '../constants/orderStatuses';
import { unwrapSupabaseJoin } from '../utils/supabase';

// ── Order select ───────────────────────────────────────────────────────────────

const ORDER_SELECT = `
    id, reference, status, status_id, type, created_at,
    customer_id, delivery_address_id, store_id,
    parcel_count, total, delivery_fee, delivery_type,
    notes, collection_point_id, shared_basket_id,
    customer:customers (
        id, first_name, last_name, cellphone, delivery_address
    ),
    store:stores ( id, name )
`;

function normaliseRow(row: OrderPageRow): Order {
    const customer = unwrapSupabaseJoin(row.customer);
    return {
        ...row,
        customer,
        deliveries: null,
        notes: row.notes ?? '',
        status_sort_order: row.status_sort_order ?? 0,
        delivery_slot_start: row.delivery_slot_start ?? '',
        delivery_slot_end: row.delivery_slot_end ?? '',
        delivery_slot_display_sast: row.delivery_slot_display_sast ?? '',
        status_obj: {
            id: row.status_id ?? '',
            name: row.status ?? '',
            sort_order: row.status_sort_order ?? 0,
        },
    };
}

// ── Types ──────────────────────────────────────────────────────────────────────

export type FetchOrdersPageParams = {
    page: number;
    pageSize: number;
    activeTab: 'open' | 'delivered';
    statuses?: string[];
    type?: string;
    deliveryType?: string;
    search?: string;
};

export type HomeOrderCounts = {
    open: number;
    awaitingPacking: number;
    packerAssigned: number;
};

export type CollectionOrder = {
    id: string;
    reference: string;
    status: string;
    pin_code: string | null;
    created_at: string;
    delivery_type: string;
    collection_point_id: string | null;
    customers: { first_name: string; last_name: string } | null;
    collection_point: { name: string; address_line1: string } | null;
};

// ── Orders page ────────────────────────────────────────────────────────────────

// Fetches a paginated, filtered page of orders for the Orders list view.
// Supports tab switching (open vs delivered), status multi-select, order type,
// delivery type, and reference search. Returns the rows and total count for pagination.
export async function fetchOrdersPageFromDb(
    params: FetchOrdersPageParams
): Promise<{ rows: Order[]; count: number }> {
    const {
        page, pageSize, activeTab,
        statuses = [], type = 'All',
        deliveryType = 'All', search = '',
    } = params;

    const from = (page - 1) * pageSize;
    const to   = from + pageSize - 1;

    let query = supabase
        .from('orders')
        .select(ORDER_SELECT, { count: 'exact' })
        .in('type', ['chat', 'ecom'])
        .order('created_at', { ascending: false })
        .range(from, to);

    if (activeTab === 'delivered') {
        query = query.in('status', [ORDER_STATUS.DELIVERY_COMPLETE, ORDER_STATUS.COLLECTION_COMPLETE]);
    } else if (statuses.length > 0) {
        query = query.in('status', statuses);
    } else {
        query = query.in('status', OPEN_ORDER_STATUSES);
    }

    if (type !== 'All') query = query.eq('type', type.toLowerCase());

    if (deliveryType && deliveryType !== 'All') {
        if (deliveryType === 'collection') query = query.eq('delivery_type', 'collection');
        else if (deliveryType === 'delivery') query = query.neq('delivery_type', 'collection');
    }

    if (search.trim()) query = query.ilike('reference', `%${search.trim()}%`);

    const { data, error, count } = await query;
    if (error) throw error;

    return {
        rows:  (data ?? []).map(normaliseRow),
        count: count ?? 0,
    };
}

// ── Create order ───────────────────────────────────────────────────────────────

// Creates a new order record with a generated 4-digit PIN code and CREATED status.
// Also writes the initial status history entry so the audit trail starts immediately.
export async function createOrderInDb(order: OrderFormData): Promise<Order> {
    const { data: statusData } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', ORDER_STATUS.CREATED)
        .single();

    const payload = {
        ...order,
        status_id: statusData?.id,
        status:    ORDER_STATUS.CREATED,
        pin_code:  Math.floor(1000 + Math.random() * 9000).toString(),
    };

    const { data, error } = await supabase
        .from('orders')
        .insert([payload])
        .select()
        .single();

    if (error) throw error;

    if (statusData?.id) {
        await supabase.from('order_status_history').insert([{
            order_id:  data.id,
            status_id: statusData.id,
        }]);
    }

    return data as Order;
}

// ── Update order status ────────────────────────────────────────────────────────

// Updates an order's status via the 'update-order-status' Supabase Edge Function.
// Using a function (rather than a direct update) ensures side-effects like
// notifications and status history writes are handled server-side consistently.
export async function updateOrderStatusInDb(
    orderId: string,
    statusName: string,
    metadata?: { model_id: string; model_type: string }
): Promise<boolean> {
    const { data, error } = await supabase.functions.invoke('update-order-status', {
        body: {
            record: {
                id:         orderId,
                status:     statusName,
                model_id:   metadata?.model_id,
                model_type: metadata?.model_type,
            },
        },
    });

    if (error) throw error;
    return data?.success ?? false;
}

// ── Statuses ───────────────────────────────────────────────────────────────────

// Fetches all available order statuses from the statuses table.
// Used to resolve status names to their UUIDs when creating or updating orders.
export async function fetchAllStatusesFromDb(): Promise<{ id: string; name: string }[]> {
    const { data, error } = await supabase.from('statuses').select('id, name');
    if (error) throw error;
    return (data ?? []) as { id: string; name: string }[];
}

// ── Home / overview ────────────────────────────────────────────────────────────

// Fetches summary counts for the home dashboard: total open orders,
// orders awaiting packing, and orders with a packer assigned.
export async function fetchHomeOrderCountsFromDb(): Promise<HomeOrderCounts> {
    const { data, error } = await supabase
        .from('orders')
        .select('id, status')
        .in('status', OPEN_ORDER_STATUSES)
        .in('type', ['ecom', 'chat']);

    if (error) throw error;

    const rows = data ?? [];
    return {
        open:            rows.length,
        awaitingPacking: rows.filter(r => r.status === ORDER_STATUS.AWAITING_PACKING).length,
        packerAssigned:  rows.filter(r => r.status === ORDER_STATUS.PACKER_ASSIGNED).length,
    };
}

// Fetches the most recent 25 orders sitting in ASSIGN_DRIVER status.
// Used by the dispatch page to show which orders are waiting for a driver.
export async function fetchOrdersNeedingDriverFromDb(): Promise<Order[]> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            id, reference, status, status_id, type, created_at,
            customer_id, delivery_address_id, store_id,
            customer:customers ( id, first_name, last_name, delivery_address )
        `)
        .in('type', ['chat', 'ecom'])
        .eq('status', ORDER_STATUS.ASSIGN_DRIVER)
        .order('created_at', { ascending: false })
        .limit(25);

    if (error) throw error;

    return (data ?? []).map((row: OrdersNeedingDriverRow): Order => {
        const customer = unwrapSupabaseJoin(row.customer);
        return {
            ...row,
            customer,
            deliveries:                null,
            parcel_count:              0,
            delivery_fee:              0,
            delivery_type:             '',
            notes:                     '',
            total:                     0,
            collection_point_id:       null,
            delivery_slot_start:       '',
            delivery_slot_end:         '',
            delivery_slot_display_sast: '',
            status_sort_order:         0,
            status_obj: {
                id:         row.status_id ?? '',
                name:       row.status ?? '',
                sort_order: 0,
            },
        };
    });
}

// ── Order detail ───────────────────────────────────────────────────────────────

// Fetches a single order by ID with full detail: status object, customer record,
// and the customer's primary delivery address resolved via the FK join.
export async function fetchSingleOrderFromDb(orderId: string): Promise<Order | null> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            *,
            status_obj:statuses!status_id (id, name, sort_order),
            customer:customers(
                *,
                primary_address:addresses!customers_delivery_address_fkey (
                    id, address_1, suburb, city, postcode
                )
            )
        `)
        .eq('id', orderId)
        .single();

    if (error) throw error;

    const order = data as Order;
    if (order?.customer) {
        order.customer.primary_address = unwrapSupabaseJoin(order.customer.primary_address);
    }
    return order;
}

// Fetches all active (non-deleted) line items for an order, joining in the
// product name, SKU, and current unit price. Deleted items are excluded via
// the deleted_at IS NULL filter — use fetchOrderItemsHistoryFromDb for the full audit trail.
// Deli/scan fields (actual_price_cents, actual_weight_kg, scanned_barcode,
// actual_scanned_at) are included so the UI can show variable-weight scan details.
// Also merges in the item's latest picking_session_items status; total_price
// is capped to the quantity actually picked when picking fell short (PARTIAL/
// MISSING), so callers summing total_price get the real billable amount —
// item.quantity itself is left as the originally ordered quantity.
export async function fetchOrderItemsFromDb(orderId: string): Promise<OrderItem[]> {
    const { data, error } = await supabase
        .from('order_items')
        .select(`
            id, order_id, product_id, quantity, created_at, product_prices_id,
            actual_weight_kg, actual_price_cents, scanned_barcode, actual_scanned_at,
            price_record:product_prices (price),
            product:products (name, sku)
        `)
        .eq('order_id', orderId)
        .is('deleted_at', null);

    if (error) throw error;

    const itemIds = (data ?? []).map((item: OrderItemRow) => item.id);
    const pickingByItemId = await fetchLatestPickingStatusForItemsFromDb(itemIds);

    return (data ?? []).map((item: OrderItemRow) => {
        const priceRecord = unwrapSupabaseJoin(item.price_record);
        const product     = unwrapSupabaseJoin(item.product);
        const estimatedUnitPrice = Number(priceRecord?.price ?? 0);
        const picking = pickingByItemId[item.id] ?? null;

        // If this line was deli-scanned, actual_price_cents is what the
        // customer is ACTUALLY charged — it overrides the estimate for
        // billing purposes. The estimate is still returned separately
        // (estimated_unit_price) so the UI can show both.
        const hasActualPrice = item.actual_price_cents != null;
        const effectiveUnitPrice = hasActualPrice ? Number(item.actual_price_cents) : estimatedUnitPrice;

        // If picking came up short (PARTIAL/MISSING — e.g. out of stock),
        // the customer should only be charged for units actually picked,
        // not the full ordered quantity. This doesn't apply to deli lines:
        // those are always quantity 1 and actual_price_cents already IS
        // the real charge, so there's nothing further to cap.
        const isPickingShortfall = !hasActualPrice
            && picking != null
            && (picking.status === 'PARTIAL' || picking.status === 'MISSING');
        const billedQuantity = isPickingShortfall ? picking!.quantity_picked : item.quantity;

        // actual_price_cents already represents the total for that one
        // scanned pack (not a per-kg rate), and quantity for these lines
        // is always 1 (see scan-picking-item) — so effective total is
        // the actual price directly, not actual_price_cents * quantity.
        const effectiveTotalPrice = hasActualPrice
            ? Number(item.actual_price_cents)
            : estimatedUnitPrice * billedQuantity;

        return {
            id:                   item.id,
            order_id:             item.order_id,
            product_id:           item.product_id,
            product_name:         product?.name ?? 'Unknown Product',
            sku:                  product?.sku  ?? 'N/A',
            quantity:             item.quantity,
            original_quantity:    item.quantity,
            unit_price:           effectiveUnitPrice,
            total_price:          effectiveTotalPrice,
            created_at:           item.created_at,
            estimated_unit_price: estimatedUnitPrice,
            actual_weight_kg:     item.actual_weight_kg ?? null,
            actual_price_cents:   item.actual_price_cents ?? null,
            scanned_barcode:      item.scanned_barcode ?? null,
            actual_scanned_at:    item.actual_scanned_at ?? null,
            picking_status:             picking?.status ?? null,
            picking_quantity_required:  picking?.quantity_required ?? null,
            picking_quantity_picked:    picking?.quantity_picked ?? null,
        };
    });
}

// Fetches the most recent picking_session_items row per order_item_id.
// An item can be picked across multiple picking sessions/attempts (e.g. a
// re-pick after a prior session left it PARTIAL or MISSING), so we keep only
// the latest row per order_item_id (by updated_at) as the current state.
async function fetchLatestPickingStatusForItemsFromDb(
    orderItemIds: string[]
): Promise<Record<string, PickingSessionItemRow>> {
    if (orderItemIds.length === 0) return {};

    const { data, error } = await supabase
        .from('picking_session_items')
        .select('order_item_id, quantity_required, quantity_picked, status, updated_at')
        .in('order_item_id', orderItemIds);

    if (error) throw error;

    const latestByItemId: Record<string, PickingSessionItemRow> = {};
    for (const row of (data ?? []) as PickingSessionItemRow[]) {
        const existing = latestByItemId[row.order_item_id];
        if (!existing || new Date(row.updated_at) > new Date(existing.updated_at)) {
            latestByItemId[row.order_item_id] = row;
        }
    }
    return latestByItemId;
}

// Fetches the full audit trail of changes to order items: quantity edits and deletions.
// Ordered ascending so the earliest entry per item gives the original value before edits.
// Used by OrderDetailsTab to show 'was N' badges and greyed-out deleted rows.
export async function fetchOrderItemsHistoryFromDb(orderId: string): Promise<OrderItemHistory[]> {
    const { data, error } = await supabase
        .from('order_items_history')
        .select('id, order_id, item_id, column, previous, current, reason, changed_by, created_at')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

    if (error) throw error;
    return (data ?? []) as OrderItemHistory[];
}

// Fetches all payment records for an order, joining in the status name from the statuses table.
export async function fetchOrderPaymentsFromDb(orderId: string): Promise<Payment[]> {
    const { data, error } = await supabase
        .from('payments')
        .select('*, status:statuses(name)')
        .eq('order_id', orderId);

    if (error) throw error;
    return (data ?? []) as Payment[];
}

// Fetches wallet credit/debit transactions linked to an order, newest first.
// Used on the order detail Payments tab to show wallet balance movements.
export async function fetchWalletTransactionsFromDb(orderId: string): Promise<WalletTransaction[]> {
    const { data, error } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('source_order_id', orderId)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as WalletTransaction[];
}

// Soft-deletes an order item via the 'admin-change-order' Edge Function.
// Uses a raw fetch (not supabase.functions.invoke) to get the exact HTTP status
// for better error messaging. Writes a deletion record to order_items_history.
export async function deleteOrderItemFromDb(
    orderId: string,
    itemId: string,
    reason: string
): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id ?? 'system';
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;

    const response = await fetch(`${supabaseUrl}/functions/v1/admin-change-order`, {
        method:  'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization:  `Bearer ${session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
            order_id:   orderId,
            item_id:    itemId,
            reason,
            action:     'delete',
            changed_by: adminId,
        }),
    });

    if (!response.ok) {
        const text = await response.text();
        let message = `HTTP ${response.status}: ${text}`;
        try {
            const json = JSON.parse(text);
            if (json.error) message = json.error;
        } catch { /* not JSON */ }
        throw new Error(message);
    }
}

// Submits a quantity change for an order item via the 'admin-change-order' Edge Function.
// Records the previous and new quantity in order_items_history for the audit trail.
export async function submitOrderItemEditToDb(
    orderId: string,
    itemId: string,
    previousQuantity: number,
    newQuantity: number,
    reason: string
): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    const adminId = session?.user?.id ?? 'system';

    const { error } = await supabase.functions.invoke('admin-change-order', {
        body: {
            order_id:   orderId,
            item_id:    itemId,
            previous:   previousQuantity,
            current:    newQuantity,
            reason,
            action:     'quantity_change',
            changed_by: adminId,
        },
    });

    if (error) throw new Error(error.message ?? 'Failed to update item');
}

// Fetches the full status history for an order, ordered chronologically.
// Each entry joins the statuses table to resolve the human-readable status name and type.
// Used on the Status & Logs tab to show the order's lifecycle timeline.
export async function fetchOrderStatusHistoryFromDb(orderId: string): Promise<OrderStatus[]> {
    const { data, error } = await supabase
        .from('order_status_history')
        .select(`
            id, status_id, created_at, model_id, model_type,
            statuses (id, name, sort_order, type)
        `)
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

    if (error) throw error;

    return (data ?? []).map((item) => {
        const statuses = item.statuses;
        const status   = Array.isArray(statuses) ? statuses[0] ?? null : statuses ?? null;
        return {
            id:         item.id,
            status_id:  item.status_id,
            created_at: item.created_at,
            model_id:   item.model_id,
            model_type: item.model_type,
            status,
        };
    }) as OrderStatus[];
}

// Fetches orders that have no associated delivery record yet, via a DB view.
// Used by the dispatch / driver assignment flow to surface orders needing action.
export async function fetchOrdersWithoutDeliveriesFromDb(): Promise<Order[]> {
    const { data, error } = await supabase
        .from('orders_without_deliveries')
        .select(`*, customer:customers(*)`)
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as Order[];
}

// ── Collections ────────────────────────────────────────────────────────────────

// Fetches all collection-type orders (delivery_type = 'collection'), newest first.
// Joins the customer name and collection point details for display in the Collections page.
export async function fetchCollectionOrdersFromDb(): Promise<CollectionOrder[]> {
    const { data, error } = await supabase
        .from('orders')
        .select(`
            id, reference, status, pin_code, created_at, delivery_type, collection_point_id,
            customers:customer_id ( first_name, last_name ),
            collection_point:collection_point_id ( name, address_line1 )
        `)
        .eq('delivery_type', 'collection')
        .order('created_at', { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as CollectionOrder[];
}

// ── Cake orders ────────────────────────────────────────────────────────────────

// Fetches all cake orders with their linked parent order details (reference, status,
// PIN code, and customer). Does a two-step fetch: cake_orders first, then a batched
// orders lookup by ID to avoid N+1 queries. Used by the Cake Collections page.
export async function fetchCakeOrdersFromDb(): Promise<CakeOrderWithOrder[]> {
    const { data: cakeOrders, error } = await supabase
        .from('cake_orders')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) throw error;
    if (!cakeOrders || cakeOrders.length === 0) return [];

    const orderIds = [...new Set(cakeOrders.map(c => c.order_id))];

    const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('id, reference, status, pin_code, customer:customers(first_name, last_name, cellphone)')
        .in('id', orderIds);

    if (orderError) throw orderError;

    const orderMap: Record<string, CakeLinkedOrder> = {};
    (orderData ?? []).forEach((o: {
        id: string;
        reference: string;
        status: string;
        pin_code: string | null;
        customer: CakeOrderCustomer | CakeOrderCustomer[] | null;
    }) => {
        orderMap[o.id] = {
            id:        o.id,
            reference: o.reference,
            status:    o.status,
            pin_code:  o.pin_code ?? null,
            customer:  unwrapSupabaseJoin(o.customer),
        };
    });

    return cakeOrders.map(c => ({ ...c, order: orderMap[c.order_id] ?? null }));
}

// Updates the status of the parent order linked to a cake order.
// Also writes a status history entry to maintain the audit trail.
export async function updateCakeOrderStatusInDb(
    orderId: string,
    statusName: string
): Promise<void> {
    const { data: statusRow } = await supabase
        .from('statuses')
        .select('id')
        .eq('name', statusName)
        .single();

    const updatePayload: { status: string; status_id?: string } = { status: statusName };
    if (statusRow) updatePayload.status_id = statusRow.id;

    const { error } = await supabase
        .from('orders')
        .update(updatePayload)
        .eq('id', orderId);

    if (error) throw error;

    if (statusRow) {
        await supabase.from('order_status_history').insert({
            order_id:  orderId,
            status_id: statusRow.id,
        });
    }
}

// ── Cake sizes ─────────────────────────────────────────────────────────────────

// Fetches all cake sizes ordered by sort_order. Used by the cake config page
// and the Build my Cake flow to populate size options.
export async function fetchCakeSizesFromDb(): Promise<CakeSize[]> {
    const { data, error } = await supabase
        .from('cake_sizes')
        .select('*')
        .order('sort_order', { ascending: true });
    if (error) throw error;
    return (data ?? []) as CakeSize[];
}

// Inserts a new cake size. Defaults active to true on creation.
export async function createCakeSizeInDb(payload: CakeSizePayload): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .insert({ ...payload, active: true });
    if (error) throw error;
}

// Updates an existing cake size's label, serves, price, and sort order.
export async function updateCakeSizeInDb(id: string, payload: CakeSizePayload): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .update(payload)
        .eq('id', id);
    if (error) throw error;
}

// Toggles a cake size's active flag. Inactive sizes are hidden from the ordering flow.
export async function toggleCakeSizeActiveInDb(id: string, active: boolean): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .update({ active })
        .eq('id', id);
    if (error) throw error;
}

// Permanently deletes a cake size. Only safe if no cake orders reference it.
export async function deleteCakeSizeFromDb(id: string): Promise<void> {
    const { error } = await supabase
        .from('cake_sizes')
        .delete()
        .eq('id', id);
    if (error) throw error;
}

// ── Cake options ───────────────────────────────────────────────────────────────

// Fetches all cake options ordered by category then sort_order.
// Options are grouped by category (e.g. Filling, Icing) in the config UI.
export async function fetchCakeOptionsFromDb(): Promise<CakeOption[]> {
    const { data, error } = await supabase
        .from('cake_options')
        .select('*')
        .order('category')
        .order('sort_order');
    if (error) throw error;
    return (data ?? []) as CakeOption[];
}

// Fetches all cake option prices. Each option can have per-size prices
// (size_id set) or a flat rate (size_id null) depending on the option's flat_rate flag.
export async function fetchCakeOptionPricesFromDb(): Promise<CakeOptionPrice[]> {
    const { data, error } = await supabase
        .from('cake_option_prices')
        .select('*');
    if (error) throw error;
    return (data ?? []) as CakeOptionPrice[];
}

// Creates a new cake option and inserts its associated price records in one operation.
// The option ID from the insert is used to link the prices.
export async function createCakeOptionInDb(
    payload: CakeOptionPayload,
    prices: CakeOptionPriceUpsert[]
): Promise<void> {
    const { data, error } = await supabase
        .from('cake_options')
        .insert({ ...payload, active: true })
        .select('id')
        .single();
    if (error) throw error;

    if (prices.length > 0) {
        const { error: priceError } = await supabase
            .from('cake_option_prices')
            .insert(prices.map(p => ({ ...p, option_id: data.id })));
        if (priceError) throw priceError;
    }
}

// Updates a cake option's metadata and replaces all its price records.
// Uses delete-then-reinsert rather than tracking individual price changes —
// simpler and safe since the caller always passes the complete price list.
export async function updateCakeOptionInDb(
    id: string,
    payload: CakeOptionPayload,
    prices: CakeOptionPriceUpsert[]
): Promise<void> {
    const { error } = await supabase
        .from('cake_options')
        .update(payload)
        .eq('id', id);
    if (error) throw error;

    await supabase.from('cake_option_prices').delete().eq('option_id', id);

    if (prices.length > 0) {
        const { error: priceError } = await supabase
            .from('cake_option_prices')
            .insert(prices.map(p => ({ ...p, option_id: id })));
        if (priceError) throw priceError;
    }
}

// Toggles a cake option's active flag. Inactive options are hidden from the ordering flow.
export async function toggleCakeOptionActiveInDb(id: string, active: boolean): Promise<void> {
    const { error } = await supabase
        .from('cake_options')
        .update({ active })
        .eq('id', id);
    if (error) throw error;
}

// Permanently deletes a cake option and all its associated price records.
// Prices are deleted first to avoid FK constraint violations.
export async function deleteCakeOptionFromDb(id: string): Promise<void> {
    await supabase.from('cake_option_prices').delete().eq('option_id', id);
    const { error } = await supabase.from('cake_options').delete().eq('id', id);
    if (error) throw error;
}

// ── Cake order by order_id ─────────────────────────────────────────────────────

// Fetches the cake order linked to a given order ID, if one exists.
// Returns null if the order has no associated cake order.
// Used on the order detail page to show the cake section.
export async function fetchCakeOrderByOrderIdFromDb(orderId: string): Promise<CakeOrder | null> {
    const { data, error } = await supabase
        .from('cake_orders')
        .select('*')
        .eq('order_id', orderId)
        .maybeSingle();
    if (error) throw error;
    return data as CakeOrder | null;
}

// ── Order items ────────────────────────────────────────────────────────────────

// Looks up the price record for a product at a specific store.
// Returns null (rather than throwing) when no price exists so the
// caller can handle missing prices per-item without aborting the whole order.
export async function fetchProductPriceRecordFromDb(
    productId: string,
    storeId: string
): Promise<{ id: string; price: number } | null> {
    const { data, error } = await supabase
        .from('product_prices')
        .select('id, price')
        .eq('product_id', productId)
        .eq('store_id', storeId)
        .single();

    if (error) return null;
    return data as { id: string; price: number };
}

// Inserts a single line item into order_items, linking the order,
// product, price record, and quantity. Throws on error so the caller
// can catch per-item failures independently.
export async function insertOrderItemFromDb(
    orderId: string,
    productId: string,
    priceRecordId: string,
    quantity: number
): Promise<void> {
    const { error } = await supabase
        .from('order_items')
        .insert({
            order_id:          orderId,
            product_id:        productId,
            product_prices_id: priceRecordId,
            quantity,
        });

    if (error) throw error;
}

// Given a list of order IDs, returns the subset that have a linked cake order.
// Returns a Set for O(1) lookups. Used on the orders list to badge rows with 'Includes Cake'.
export async function fetchCakeOrderIdsByOrderIdsFromDb(orderIds: string[]): Promise<Set<string>> {
    const { data, error } = await supabase
        .from('cake_orders')
        .select('order_id')
        .in('order_id', orderIds);

    if (error) throw error;
    return new Set((data ?? []).map(r => String(r.order_id)));
}

// ── Cake collection completion ─────────────────────────────────────────────────

// Stamps collected_at on a cake order to mark it as handed over to the customer.
// PIN verification happens in the component before this is called.
// Returns silently on success; throws on DB error.
export async function markCakeOrderCollectedInDb(cakeOrderId: string): Promise<void> {
    const { error } = await supabase
        .from('cake_orders')
        .update({ collected_at: new Date().toISOString() })
        .eq('id', cakeOrderId)
        .select();

    if (error) throw error;
}